// frontend/src/pages/OfferingResults.js

import React, { useEffect, useMemo, useState } from "react";
import api from "../api";

const styles = {
  page: {
    maxWidth: 1200,
    margin: "0 auto",
    display: "grid",
    gap: 16,
  },
  card: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    padding: 18,
    boxShadow: "0 10px 25px rgba(15,23,42,0.04)",
  },
  titleRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
    flexWrap: "wrap",
  },
  h2: {
    margin: 0,
    fontSize: 24,
    fontWeight: 900,
    color: "#111827",
  },
  h3: {
    margin: 0,
    fontSize: 18,
    fontWeight: 900,
    color: "#111827",
  },
  muted: {
    color: "#6b7280",
    fontSize: 13,
    lineHeight: 1.5,
  },
  select: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid #d1d5db",
    background: "#fff",
    outline: "none",
    boxSizing: "border-box",
  },
  btn: {
    padding: "10px 14px",
    borderRadius: 12,
    border: "1px solid #d1d5db",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 800,
  },
  btnPrimary: {
    padding: "10px 14px",
    borderRadius: 12,
    border: "1px solid #1d4ed8",
    background: "#2563eb",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 800,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "5px 10px",
    fontSize: 12,
    fontWeight: 800,
    background: "#f9fafb",
    color: "#374151",
    border: "1px solid #e5e7eb",
    whiteSpace: "nowrap",
  },
  goodChip: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "5px 10px",
    fontSize: 12,
    fontWeight: 800,
    background: "#f0fdf4",
    color: "#166534",
    border: "1px solid #bbf7d0",
    whiteSpace: "nowrap",
  },
  badChip: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "5px 10px",
    fontSize: 12,
    fontWeight: 800,
    background: "#fff1f2",
    color: "#991b1b",
    border: "1px solid #fecaca",
    whiteSpace: "nowrap",
  },
  warnChip: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "5px 10px",
    fontSize: 12,
    fontWeight: 800,
    background: "#fffbeb",
    color: "#92400e",
    border: "1px solid #fde68a",
    whiteSpace: "nowrap",
  },
  alert: {
    border: "1px solid #fecaca",
    background: "#fff1f2",
    color: "#991b1b",
    borderRadius: 14,
    padding: 12,
    fontWeight: 700,
  },
  statGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 12,
  },
  stat: {
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    padding: 14,
    background: "#f9fafb",
  },
  statValue: {
    fontSize: 22,
    fontWeight: 900,
    color: "#111827",
    marginTop: 6,
  },
  tableWrap: {
    overflowX: "auto",
    border: "1px solid #e5e7eb",
    borderRadius: 14,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    textAlign: "left",
    padding: 12,
    background: "#f9fafb",
    borderBottom: "1px solid #e5e7eb",
    color: "#374151",
    fontSize: 13,
    whiteSpace: "nowrap",
  },
  td: {
    padding: 12,
    borderBottom: "1px solid #f3f4f6",
    color: "#111827",
    verticalAlign: "top",
    fontSize: 14,
  },
};

function formatOffering(o) {
  if (!o) return "Select offering";

  return `${o.course_code || "Course"} — ${o.course_name || ""} | ${
    o.semester || "-"
  } ${o.session || "-"} | ${o.batch || "-"} ${o.section || "-"} | ${
    o.instructor_name || "Instructor"
  }`;
}

function getBackendError(err, fallback) {
  const detail = err?.response?.data?.detail;

  if (Array.isArray(detail)) {
    return detail.map((x) => x?.msg || JSON.stringify(x)).join("\n");
  }

  if (typeof detail === "string") return detail;

  return fallback;
}

function percentText(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "-";
  return `${Number(v).toFixed(1)}%`;
}

function marksText(obtained, total) {
  return `${Number(obtained || 0).toFixed(1)} / ${Number(total || 0).toFixed(
    1
  )}`;
}

function statusChip(status) {
  const s = String(status || "").toLowerCase();

  if (s === "graded") return styles.goodChip;
  if (s === "missed") return styles.badChip;
  if (s === "error") return styles.badChip;

  return styles.warnChip;
}

export default function OfferingResults() {
  const [offerings, setOfferings] = useState([]);
  const [offeringId, setOfferingId] = useState("");
  const [data, setData] = useState(null);

  const [loadingOfferings, setLoadingOfferings] = useState(false);
  const [loadingResults, setLoadingResults] = useState(false);
  const [err, setErr] = useState("");

  const selectedOffering = useMemo(
    () => offerings.find((x) => x.id === offeringId),
    [offerings, offeringId]
  );

  const cloKeys = useMemo(() => {
    const students = data?.students || [];

    return Array.from(
      new Set(students.flatMap((s) => Object.keys(s.clos || {})))
    ).sort();
  }, [data]);

  const loadOfferings = async () => {
    setErr("");
    setLoadingOfferings(true);

    try {
      const res = await api.get("/course-offerings/my");
      const list = res.data || [];

      setOfferings(list);

      if (!offeringId && list.length) {
        setOfferingId(list[0].id);
      }
    } catch (e) {
      setErr(getBackendError(e, "Failed to load course offerings"));
    } finally {
      setLoadingOfferings(false);
    }
  };

  const loadResults = async (targetId = offeringId) => {
    if (!targetId) {
      setData(null);
      return;
    }

    setErr("");
    setLoadingResults(true);
    setData(null);

    try {
      const res = await api.get(
        `/offering-results/offerings/${targetId}/student-results`
      );

      setData(res.data || null);
    } catch (e) {
      setErr(getBackendError(e, "Failed to load result report"));
    } finally {
      setLoadingResults(false);
    }
  };

  useEffect(() => {
    loadOfferings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!offeringId) return;

    loadResults(offeringId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offeringId]);

  const exportCsv = () => {
    const students = data?.students || [];

    const headers = [
      "Roll No",
      "Student Name",
      "Submitted",
      "Missed",
      "Total Assessments",
      "Obtained Marks",
      "Total Marks",
      "Average %",
      ...cloKeys.map((c) => `${c} %`),
    ];

    const lines = [
      headers.join(","),
      ...students.map((s) =>
        [
          s.roll_no || "",
          `"${String(s.student_name || "").replaceAll('"', '""')}"`,
          s.submitted ?? 0,
          s.missed ?? 0,
          s.total_assessments ?? 0,
          s.obtained_marks ?? 0,
          s.total_marks ?? 0,
          s.average_percent ?? "",
          ...cloKeys.map((c) => s.clos?.[c]?.attainment_percent ?? ""),
        ].join(",")
      ),
    ];

    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "offering_student_results.csv";
    a.click();

    window.URL.revokeObjectURL(url);
  };

  const summary = data?.summary || {};

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.titleRow}>
          <div>
            <h2 style={styles.h2}>Student Results & CLO Progress</h2>
            <p style={{ ...styles.muted, marginTop: 6 }}>
              Tracks enrolled students by roll number, submitted/missed
              assessments, total result average, and CLO-wise academic progress.
            </p>
          </div>

          <button
            type="button"
            style={styles.btn}
            onClick={() => loadResults(offeringId)}
            disabled={!offeringId || loadingResults}
          >
            {loadingResults ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        {err && <div style={{ ...styles.alert, marginTop: 12 }}>{err}</div>}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: 12,
            alignItems: "end",
            marginTop: 14,
          }}
        >
          <div>
            <label style={{ ...styles.muted, fontWeight: 900 }}>
              Course Offering / Class Section
            </label>

            <select
              style={{ ...styles.select, marginTop: 6 }}
              value={offeringId}
              onChange={(e) => {
                setOfferingId(e.target.value);
                setData(null);
              }}
              disabled={loadingOfferings || loadingResults}
            >
              <option value="">
                {loadingOfferings
                  ? "Loading offerings..."
                  : "Select offering..."}
              </option>

              {offerings.map((o) => (
                <option key={o.id} value={o.id}>
                  {formatOffering(o)}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            style={styles.btnPrimary}
            onClick={exportCsv}
            disabled={!data?.students?.length}
          >
            Export CSV
          </button>
        </div>

        {selectedOffering && (
          <div
            style={{
              marginTop: 12,
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <span style={styles.chip}>
              {selectedOffering.course_code} — {selectedOffering.course_name}
            </span>
            <span style={styles.chip}>
              {selectedOffering.semester} {selectedOffering.session}
            </span>
            <span style={styles.chip}>
              {selectedOffering.batch} {selectedOffering.section}
            </span>
            <span style={styles.goodChip}>
              Instructor: {selectedOffering.instructor_name || "-"}
            </span>
            <span style={styles.chip}>
              Students: {selectedOffering.students_count ?? 0}
            </span>
          </div>
        )}
      </div>

      <div style={styles.statGrid}>
        <div style={styles.stat}>
          <div style={styles.muted}>Students</div>
          <div style={styles.statValue}>{summary.students ?? 0}</div>
        </div>

        <div style={styles.stat}>
          <div style={styles.muted}>Assessments</div>
          <div style={styles.statValue}>{summary.assessments ?? 0}</div>
        </div>

        <div style={styles.stat}>
          <div style={styles.muted}>Class Average</div>
          <div style={styles.statValue}>
            {percentText(summary.class_average_percent)}
          </div>
        </div>

        <div style={styles.stat}>
          <div style={styles.muted}>Submitted / Missed</div>
          <div style={styles.statValue}>
            {summary.submitted ?? 0} / {summary.missed ?? 0}
          </div>
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.titleRow}>
          <div>
            <h3 style={styles.h3}>Offering-Level CLO Attainment</h3>
            <p style={{ ...styles.muted, marginTop: 4 }}>
              Average CLO achievement calculated from graded submissions in this
              offering.
            </p>
          </div>
        </div>

        <div
          style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}
        >
          {Object.keys(summary.offering_clo_attainment || {}).map((clo) => {
            const val = summary.offering_clo_attainment[clo];

            return (
              <span key={clo} style={styles.goodChip}>
                {clo}: {percentText(val?.attainment_percent)}
              </span>
            );
          })}

          {!Object.keys(summary.offering_clo_attainment || {}).length && (
            <span style={styles.chip}>
              No CLO attainment yet. Grade assessments first.
            </span>
          )}
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.titleRow}>
          <div>
            <h3 style={styles.h3}>Student Result Tracker</h3>
            <p style={{ ...styles.muted, marginTop: 4 }}>
              Shows each enrolled student, missed assessments, marks, average,
              and CLO progress.
            </p>
          </div>

          {loadingResults && <span style={styles.chip}>Loading report...</span>}
        </div>

        <div style={{ ...styles.tableWrap, marginTop: 14 }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Roll No</th>
                <th style={styles.th}>Student</th>
                <th style={styles.th}>Submitted</th>
                <th style={styles.th}>Missed</th>
                <th style={styles.th}>Total Result</th>
                <th style={styles.th}>Average</th>

                {cloKeys.map((c) => (
                  <th key={c} style={styles.th}>
                    {c}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {(data?.students || []).map((s) => (
                <tr key={s.offering_student_id || s.roll_no}>
                  <td style={{ ...styles.td, fontWeight: 900 }}>
                    {s.roll_no || "-"}
                  </td>

                  <td style={styles.td}>{s.student_name || "-"}</td>

                  <td style={styles.td}>
                    <span style={styles.goodChip}>{s.submitted ?? 0}</span>
                  </td>

                  <td style={styles.td}>
                    <span style={s.missed ? styles.badChip : styles.goodChip}>
                      {s.missed ?? 0}
                    </span>
                  </td>

                  <td style={{ ...styles.td, fontWeight: 900 }}>
                    {marksText(s.obtained_marks, s.total_marks)}
                  </td>

                  <td style={styles.td}>{percentText(s.average_percent)}</td>

                  {cloKeys.map((c) => (
                    <td key={c} style={styles.td}>
                      {percentText(s.clos?.[c]?.attainment_percent)}
                    </td>
                  ))}
                </tr>
              ))}

              {!data?.students?.length && (
                <tr>
                  <td
                    colSpan={6 + cloKeys.length}
                    style={{ ...styles.td, color: "#6b7280" }}
                  >
                    {loadingResults
                      ? "Loading results..."
                      : "No enrolled students/results found. Add students to this offering and grade assessments first."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div style={styles.card}>
        <h3 style={styles.h3}>Assessment Breakdown</h3>

        <div style={{ ...styles.tableWrap, marginTop: 14 }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Assessment</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Max Marks</th>
                <th style={styles.th}>CLO Coverage</th>
              </tr>
            </thead>

            <tbody>
              {(data?.assessments || []).map((a) => (
                <tr key={a.id}>
                  <td style={{ ...styles.td, fontWeight: 900 }}>{a.title}</td>
                  <td style={styles.td}>{a.type}</td>
                  <td style={styles.td}>{a.date || "-"}</td>
                  <td style={styles.td}>{a.max_marks}</td>
                  <td style={styles.td}>
                    {percentText(a.clo_coverage_percent)}
                  </td>
                </tr>
              ))}

              {!data?.assessments?.length && (
                <tr>
                  <td colSpan={5} style={{ ...styles.td, color: "#6b7280" }}>
                    No assessments created for this offering yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {data?.students?.length ? (
        <div style={styles.card}>
          <h3 style={styles.h3}>Detailed Per-Student Assessment Status</h3>

          <div style={{ display: "grid", gap: 12, marginTop: 14 }}>
            {data.students.map((s) => (
              <details key={s.offering_student_id || s.roll_no}>
                <summary style={{ cursor: "pointer", fontWeight: 900 }}>
                  {s.roll_no} — {s.student_name} | Average:{" "}
                  {percentText(s.average_percent)}
                </summary>

                <div style={{ ...styles.tableWrap, marginTop: 10 }}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Assessment</th>
                        <th style={styles.th}>Type</th>
                        <th style={styles.th}>Status</th>
                        <th style={styles.th}>Marks</th>
                        <th style={styles.th}>Percentage</th>
                      </tr>
                    </thead>

                    <tbody>
                      {(s.assessments || []).map((a) => (
                        <tr key={a.assessment_id}>
                          <td style={styles.td}>{a.title}</td>
                          <td style={styles.td}>{a.type}</td>
                          <td style={styles.td}>
                            <span style={statusChip(a.status)}>
                              {a.status}
                            </span>
                          </td>
                          <td style={styles.td}>
                            {marksText(a.obtained_marks, a.total_marks)}
                          </td>
                          <td style={styles.td}>
                            {percentText(a.percentage)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}